import React, { useEffect, useCallback, useState, useRef } from 'react';
import cx from 'classnames';
import { LoadingSpinner } from 'src/components/docs/LoadingSpinner';

import styles from './ChatDialog.module.scss';
import ChatInput from './ChatInput';
import ChatAnswer from './ChatAnswer';
import ChatSources from './ChatSources';
import ChatActions from './ChatActions';
import DebugServerPicker from './DebugServerPicker';
import BillGraphic from './BillGraphic';
import { useChatState } from './ChatState';
import { useChatAnalytics } from './ChatAnalytics';
import { useChatMessageHandlers } from './ChatMessageHandlers';
import { PRIMER_QUESTIONS } from './constants';
import {
  getConversationIdFromUrl,
  fetchConversation,
  loadConversation,
} from './ChatLoadConversation';

const DEFAULT_INTRO_TEXT =
  "Hi! I'm Bill! You can ask me all about the Plaid API. Try asking questions like:";

interface ChatDialogProps {
  isVisible: boolean;
  // Lead-in copy shown above the primer questions in the empty state.
  introText?: React.ReactNode;
  // Pool the empty-state primer questions are sampled from.
  primerQuestions?: Array<string>;
  // Docs-section hint sent to the backend so Bill answers from the matching
  // corpus (e.g. "corex"). Empty string means the default main-docs corpus.
  section?: string;
  // Whether to show the empty-state plug for the Plaid AI coding toolkit. Off
  // for sections the toolkit's MCP server doesn't support yet (e.g. Core Exchange).
  showCodingToolkitPlug?: boolean;
}

const ChatDialog: React.FC<ChatDialogProps> = ({
  isVisible,
  introText = DEFAULT_INTRO_TEXT,
  primerQuestions = PRIMER_QUESTIONS,
  section = '',
  showCodingToolkitPlug = true,
}) => {
  // Initialize chat state
  const chatState = useChatState({
    primerQuestionPool: primerQuestions,
    section,
  });
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // Initialize analytics
  const analytics = useChatAnalytics({
    lastQuestionID: chatState.lastQuestionID,
    likelyInternal: chatState.likelyInternal,
    timeQuestionSubmitted: chatState.timeQuestionSubmitted,
    section: chatState.section,
  });

  // Initialize message handlers
  const handlers = useChatMessageHandlers({
    chatState,
    analytics,
  });

  // Store stable references to event handlers
  const handleWebsocketMessage = useCallback(handlers.handleWebsocketMessage, [
    handlers.handleWebsocketMessage,
  ]);
  const handleWebsocketClose = useCallback(handlers.handleWebsocketClose, [
    handlers.handleWebsocketClose,
  ]);
  const handleWebsocketOpen = useCallback(handlers.handleWebsocketOpen, [
    handlers.handleWebsocketOpen,
  ]);

  // // Use a ref to track if we've already loaded this conversation
  const loadedConversationRef = useRef<string | null>(null);

  // Check for conversation ID in URL and load if present
  useEffect(() => {
    const conversationId = getConversationIdFromUrl();

    // Only proceed if there's a conversation ID, a server, and we haven't loaded this conversation before
    if (
      conversationId &&
      chatState.finnServerRef.current &&
      loadedConversationRef.current !== conversationId
    ) {
      // Remember this conversation ID to prevent duplicate loads
      loadedConversationRef.current = conversationId;

      setIsLoadingConversation(true);

      fetchConversation(conversationId, chatState.finnServerRef.current)
        .then((conversationData) => {
          if (conversationData) {
            loadConversation(conversationData, chatState);
            analytics.trackConversationLoaded(conversationId);
          }
        })
        .catch((err) => {
          console.error('Error loading conversation:', err);
        })
        .finally(() => {
          setIsLoadingConversation(false);
        });
    }
  }, [chatState.finnServerRef.current, loadedConversationRef]);

  // Clean up websocket event listeners when component unmounts
  useEffect(() => {
    return () => {
      if (chatState.currentWebsocket.current === null) {
        return;
      }

      chatState.currentWebsocket.current.removeEventListener(
        'message',
        handleWebsocketMessage,
      );
      chatState.currentWebsocket.current.removeEventListener(
        'close',
        handleWebsocketClose,
      );
      chatState.currentWebsocket.current.removeEventListener(
        'open',
        handleWebsocketOpen,
      );
    };
  }, [
    chatState.currentWebsocket,
    handleWebsocketMessage,
    handleWebsocketClose,
    handleWebsocketOpen,
  ]);

  // Compute if we're in empty state
  const isEmptyState =
    chatState.lastQuestion === '' && chatState.currentAnswer === '';

  return (
    <>
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Ask Bill'
        className={cx(styles.chatDialog, !isVisible && styles.hidden)}
      >
        <form onSubmit={handlers.handleSubmit}>
          <ChatInput
            isEmptyState={isEmptyState}
            isStreaming={chatState.isStreaming}
            currentQuestion={chatState.currentQuestion}
            onInputChange={chatState.handleInputChange}
            handleStopStream={handlers.handleStopStream}
          />
        </form>
        <div className={styles.scrollableWrapper}>
          <div className={styles.topWrapper}>
            <BillGraphic billState={chatState.billState} />
            {/* Show the empty state if question is null  */}
            {isEmptyState && (
              <div className={styles.emptyState}>
                <div className={styles.speechBubble}>
                  <span className={styles.emptyStateText}>
                    {introText}
                    <ul>
                      {chatState.primerQuestions.map((q) => {
                        return <li key={q}>{q}</li>;
                      })}
                    </ul>
                    {showCodingToolkitPlug && (
                      <>
                        Pssst -- I also moonlight as your IDE's research
                        librarian! Plug me in via the{' '}
                        <a
                          href='https://github.com/plaid/ai-coding-toolkit/tree/main/sandbox'
                          target='_blank'
                          rel='noreferrer'
                        >
                          Plaid MCP Server
                        </a>
                        .
                      </>
                    )}
                  </span>
                </div>
              </div>
            )}
            {!isEmptyState && (
              <div className={styles.qAndAWrapper}>
                <div className={styles.questionTitle}>
                  {chatState.lastQuestion}
                </div>
                {isLoadingConversation ? (
                  <>
                    <div className={styles.waitingMessage}>
                      Loading conversation...
                    </div>
                    <LoadingSpinner />
                  </>
                ) : chatState.currentAnswer === '' ? (
                  <>
                    {chatState.waitingMessage !== '' && (
                      <div className={styles.waitingMessage}>
                        {chatState.waitingMessage}
                      </div>
                    )}
                    {chatState.isStreaming && <LoadingSpinner />}
                  </>
                ) : (
                  <ChatAnswer
                    currentAnswer={chatState.currentAnswer}
                    questionId={chatState.lastQuestionID.current}
                  />
                )}
              </div>
            )}
          </div>
          <div className={styles.bottomWrapper}>
            {!isEmptyState && (
              <div className={styles.sourceWrapper}>
                <ChatSources sources={chatState.answerSources} />
                <ChatActions
                  chatState={chatState}
                  feedbackHandler={handlers.handleFeedback}
                  selectedFeedback={chatState.answerFeedback}
                  markdownText={chatState.currentAnswer}
                  questionId={chatState.lastQuestionID.current}
                />
              </div>
            )}
            <DebugServerPicker
              currentServer={chatState.finnServer}
              serverSelected={handlers.handleServerSelected}
            />
            <div className={styles.disclaimer}>
              <b>Note:</b> Bill isn't perfect. He's just a robot platypus that
              reads our docs for fun. You should treat his answers with the same
              healthy skepticism you might treat any other answer on the
              internet. This chat may be logged for quality and training
              purposes. Please don't send Bill any PII -- he's scared of
              intimacy. All chats with Bill are subject to Plaid's{' '}
              <a
                href='https://plaid.com/legal/#all-audiences'
                className={styles.disclaimerLink}
                target='_blank'
                rel='noreferrer'
              >
                Privacy Policy.
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ChatDialog.displayName = 'ChatDialog';
export default ChatDialog;
